import type { SortDescriptor } from "react-aria-components";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ScrapedBrandTable } from "@/components/scraped-brand/table";
import { useGetScrapedBrands } from "@/hooks/api/scraped-brand/useGetScrapedBrands";
import { AppPage } from "@/layouts/page";

export const ScrapedBrandsPage = () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const initialPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;
  const initialLimit = searchParams.get("limit")
    ? parseInt(searchParams.get("limit")!)
    : 10;

  const [pageParams, setPageParams] = useState({
    page: initialPage,
    limit: initialLimit,
  });

  const [nameFilter, setNameFilter] = useState(searchParams.get("name") || "");
  const [supplierIdFilter, setSupplierIdFilter] = useState(
    searchParams.get("supplierId") || "",
  );
  const [blacklistFilter, setBlacklistFilter] = useState(
    searchParams.get("blacklistFilter") || "",
  );

  const initialSortBy = searchParams.get("sortBy") as
    | "createdAt"
    | "productCount"
    | null;
  const initialSortOrder = searchParams.get("sortOrder") as
    | "ASC"
    | "DESC"
    | null;

  const [sortBy, setSortBy] = useState<
    "createdAt" | "productCount" | undefined
  >(initialSortBy || undefined);
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | undefined>(
    initialSortOrder || undefined,
  );

  const { data: scrapedBrands, isFetching: isScrapedBrandsLoading } =
    useGetScrapedBrands({
      ...pageParams,
      name: nameFilter || undefined,
      supplierId: supplierIdFilter || undefined,
      blacklistOnly:
        blacklistFilter === "blacklisted"
          ? true
          : blacklistFilter === "whitelisted"
            ? false
            : undefined,
      sortBy,
      sortOrder,
    });

  useEffect(() => {
    const urlPage = searchParams.get("page");
    const urlLimit = searchParams.get("limit");

    if (urlPage) {
      const page = parseInt(urlPage);

      setPageParams((prev) => ({ ...prev, page }));
    }
    if (urlLimit) {
      const limit = parseInt(urlLimit);

      setPageParams((prev) => ({ ...prev, limit }));
    }
  }, [searchParams]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleLimitChange = (newLimit: number) => {
    searchParams.set("limit", newLimit.toString());
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleNameChange = (newName: string) => {
    setNameFilter(newName);
    if (newName) {
      searchParams.set("name", newName);
    } else {
      searchParams.delete("name");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleSupplierIdChange = (newSupplierId: string) => {
    setSupplierIdFilter(newSupplierId);
    if (newSupplierId) {
      searchParams.set("supplierId", newSupplierId);
    } else {
      searchParams.delete("supplierId");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleBlacklistFilterChange = (value: string) => {
    setBlacklistFilter(value);
    if (value && value !== "all") {
      searchParams.set("blacklistFilter", value);
    } else {
      searchParams.delete("blacklistFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleSortChange = (descriptor: SortDescriptor) => {
    const column = descriptor.column as
      | "createdAt"
      | "productCount"
      | undefined;
    const direction = descriptor.direction;

    setSortBy(column);
    setSortOrder(
      direction === "ascending"
        ? "ASC"
        : direction === "descending"
          ? "DESC"
          : undefined,
    );

    if (column) {
      searchParams.set("sortBy", column);
    } else {
      searchParams.delete("sortBy");
    }

    if (direction) {
      searchParams.set("sortOrder", direction === "ascending" ? "ASC" : "DESC");
    } else {
      searchParams.delete("sortOrder");
    }

    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const sortDescriptor: SortDescriptor | undefined =
    sortBy && sortOrder
      ? {
          column: sortBy,
          direction: sortOrder === "ASC" ? "ascending" : "descending",
        }
      : undefined;

  return (
    <AppPage
      subtitle={
        scrapedBrands ? (
          <>
            <span className="tabular-nums">
              {scrapedBrands.total.toLocaleString()}
            </span>{" "}
            brands scraped from suppliers
          </>
        ) : (
          "Manage brands scraped from suppliers"
        )
      }
      title="Scraped brands"
    >
      <ScrapedBrandTable
        blacklistFilter={blacklistFilter}
        changePage={handlePageChange}
        handleBlacklistFilterChange={handleBlacklistFilterChange}
        handleLimitChange={handleLimitChange}
        handleSearchTermChange={handleNameChange}
        handleSupplierIdChange={handleSupplierIdChange}
        isLoading={isScrapedBrandsLoading}
        items={scrapedBrands?.data ?? []}
        limit={pageParams.limit ?? 10}
        page={pageParams.page ?? 1}
        searchTerm={nameFilter}
        sortDescriptor={sortDescriptor}
        supplierId={supplierIdFilter}
        totalBrands={scrapedBrands?.total ?? 0}
        totalPage={scrapedBrands?.totalPages ?? 1}
        onSortChange={handleSortChange}
      />
    </AppPage>
  );
};
